import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import Chart from 'chart.js/auto';
import { Calendar, Users, Users2, Loader2, List, CalendarFold, Eye } from 'lucide-react';
import { useUser } from '../../context/UserContext';
import axiosInstance from '../../services/api/axios';
import DataTable from '../../components/DataTable';
import { OutlineButton } from "../../components/Buttons"
import { toast } from 'react-toastify';
import { CheckBox, CheckBoxOutlined, MonetizationOn, MonetizationOnOutlined } from '@mui/icons-material';
import LeadDetailsDrawer from './LeadDetailsDrawer';
import { Button, Spinner } from '@material-tailwind/react';
import { updateVisit } from '../../services/users';
import SummaryCard from '../../components/SummaryCard';
import CustomInput from '../../components/CustomInput';


const upcomingTimeFilterOptions = [
    { value: 'today', label: 'Today' },
    { value: 'next5Days', label: 'Next 5 Days' },
    { value: 'next2Weeks', label: 'Next 2 Weeks' },
    { value: 'all', label: 'All' }
];

const pastTimeFilterOptions = [
    { value: 'pastDay', label: 'Past Week' },
    { value: 'pastMonth', label: 'Past Month' },
    { value: 'all', label: 'All' }
];

function DashboardContent() {
    const [leadsData, setLeadsData] = useState(null);
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [totalLeads, setTotalLeads] = useState(0);
    const [scheduledVisits, setScheduledVisits] = useState(0);
    const [visits, setVisits] = useState([]);
    const [filterDate, setFilterDate] = useState(new Date().toISOString().slice(0, 10));
    const [timeFilter, setTimeFilter] = useState('today');
    const [searchQuery, setSearchQuery] = useState('');
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState('');
    const chartRef = useRef(null);
    const { userInfo } = useUser();
    const gridRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('upcoming');
    const [selectedVisitForView, setSelectedVisitForView] = useState(null);
    const [showLeadDetails, setShowLeadDetails] = useState(false);
    const [selecetedLeadsDataArray, setSlecetedLeadsDataArray] = useState(null);
    console.log("userInfo?.persona", userInfo?.persona);
    useEffect(() => {
        const fetchLeadsData = async () => {
            try {
                let params;
            
                if (userInfo?.persona === "CHANNEL_PARTNER") {
                    params = {
                        agentId: userInfo.loginIdentifierDetails.customerId
                    };
                } else {
                    params = {
                        customerId:
                            userInfo.loginIdentifierDetails.customer?.customerId ??
                            userInfo.loginIdentifierDetails.customerId,
                    };
                }
    
                const response = await axiosInstance.get(`/users/leads-bifurcation`, {
                    params,
                });
                setLeadsData(response);
                setTotalCustomers(response.customers.length);
                setTotalLeads(response ? (response.coldLeads.length + response.lukeWarmLeads.length + response.warmLeads.length + response.hotLeads.length) : 0);
                setScheduledVisits(visits.length);
            } catch (error) {
                console.error('Error fetching leads data:', error);
            }
        };

        const fetchAllProjects = async () => {
            try {
                const response = await axiosInstance.get(`/projects`, {
                    params: {
                        customerId:
                            userInfo.loginIdentifierDetails.customer?.customerId ??
                            userInfo.loginIdentifierDetails.customerId,
                    },
                });
                // Handle both paginated and non-paginated responses
                const projectsArray = response?.items || (Array.isArray(response) ? response : []);
                setProjects(projectsArray);
            } catch (error) {
                console.error('Error fetching projects:', error);
                setProjects([]);
            }
        };

        fetchLeadsData();
        fetchAllProjects();
    }, [userInfo]);

    useEffect(() => {
        if (leadsData && chartRef.current) {
           
            const chartCanvas = chartRef.current.getContext('2d');

            if (chartCanvas) {
                if (window.myPieChart) {
                    window.myPieChart.destroy();
                }

                window.myPieChart = new Chart(chartCanvas, {
                    type: 'pie',
                    data: {
                        labels: ['Cold', 'LukeWarm', 'Warm', 'Hot', 'Customers'],
                        datasets: [{
                            data: [
                                leadsData.coldLeads.length,
                                leadsData.lukeWarmLeads.length,
                                leadsData.warmLeads.length,
                                leadsData.hotLeads.length,
                                leadsData.customers.length,
                            ],
                            backgroundColor: ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'],
                        }],
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                position: 'right',
                            },
                            title: {
                                display: true,
                                text: 'Leads Bifurcation',
                            },
                        },
                    },
                });
            }
        }
        return () => {
            if (window.myPieChart) {
                window.myPieChart.destroy();
            }
        };
    }, [leadsData]);

    const fetchVisits = useCallback(async (date, timeRange, project, leadUserId) => {
        try {
            const customerId =
                userInfo.loginIdentifierDetails.customer?.customerId ??
                userInfo.loginIdentifierDetails.customerId;

            let startDate = new Date(date);
            let endDate = new Date(date);

            switch (timeRange) {

                case 'next5Days':
                    endDate.setDate(startDate.getDate() + 5);
                    break;
                case 'next2Weeks':
                    endDate.setDate(startDate.getDate() + 14);
                    break;


                case 'pastDay':
                    endDate.setDate(startDate.getDate() - 1);
                    startDate.setDate(startDate.getDate() - 1);
                    break;
                case 'pastWeek':
                    endDate.setDate(startDate.getDate() - 7);
                    startDate.setDate(startDate.getDate() - 7);
                    break;
                case 'pastMonth':
                    endDate.setMonth(startDate.getMonth() - 1);
                    startDate.setDate(startDate.getDate() - 1);
                    break;
                default:
                    break;
            }

            const formattedStartDate = startDate.toISOString().slice(0, 10);
            const formattedEndDate = endDate.toISOString().slice(0, 10);

            const isAgent = userInfo?.persona === "CHANNEL_PARTNER";
            const params = {
                agentId: isAgent ? userInfo.loginIdentifierDetails.customerId : undefined,
                customerId: isAgent ? undefined : customerId,
                userId: leadUserId
            };

            const response = await axiosInstance.get(`/users/property`, {
                params: params,
            });
         
            // Handle both paginated and non-paginated responses
            const visitsArray = response?.items || (Array.isArray(response) ? response : []);

            let filteredVisits = visitsArray.filter((visit) => {
                const visitDate = visit.visitSchedule ? visit.visitSchedule.slice(0, 10) : null;
                const visitedDateTimes = visit.visitedDateTime || [];

                if (timeRange !== 'all') {
                    let withinRange = false;
                    if (activeTab === 'upcoming' && visitDate) {
                        if (visitDate >= formattedStartDate && visitDate <= formattedEndDate) {
                            withinRange = true;
                        }
                    } else if (activeTab === 'past' && visitedDateTimes.length > 0) {
                        // Check if any visitedDateTime falls within the range
                        withinRange = visitedDateTimes.some(visitedDateTime => {
                            const visitedDate = new Date(visitedDateTime).toISOString().slice(0, 10);
                            return visitedDate >= formattedStartDate && visitedDate <= formattedEndDate;
                        });
                    }
                    if (!withinRange) return false; // Skip if no date falls within the range
                }


                if (project && project !== '') {
                    if (visit.propertyConfig?.project?.projectId != project) {
                        return false;
                    }
                }

                return true;
            });
        
            const formattedVisits = filteredVisits.map(visit => ({
                visitData: visit,
                time: visit.visitSchedule ? new Date(visit.visitSchedule).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A',
                customer: visit.user?.firstName + ' ' + visit.user?.lastName,
                mobileNoWa: visit.user?.mobileNoWa,
                configName: visit.propertyConfig?.configName,
                projectName: visit.propertyConfig?.project?.projectName,
                ownerName: visit.agent?.ownerName,
                details: visit.visitProgress,
                visited: visit.visited,

            }));
            
            setVisits(formattedVisits);
            setScheduledVisits(filteredVisits.filter(visit => visit.visited).length);
        } catch (error) {
            console.error('Error fetching visits:', error);
            setVisits([]);
            setScheduledVisits(0);
        }
    }, [userInfo, activeTab]);




    const handleDateChange = (e) => {
        const selectedDate = e.target.value;
        setFilterDate(selectedDate);
        fetchVisits(selectedDate, timeFilter, selectedProject);

    };

    const handleTimeFilterChange = (e) => {
        setTimeFilter(e.target.value);
        fetchVisits(filterDate, e.target.value, selectedProject);

    };

    const handleProjectChange = (e) => {
        setSelectedProject(e.target.value);
        fetchVisits(filterDate, timeFilter, e.target.value);

    };
    const handleMarkAsVisited = async (currentVisit, visitData) => {
        setIsLoading(true);
      
        try {
            if (visitData) {
                const updatedVisitData = {
                    userId: visitData.user?.userId,
                    customerId: visitData.customer?.customerId,
                    agentId: visitData.agent?.customerId,
                    propertyConfigId: visitData.propertyConfig?.propertyConfigId,
                    projectId: visitData.propertyConfig?.project?.projectId,
                    visitSchedule: visitData.visitSchedule, // Use the original visit schedule
                    visited: !currentVisit.visited,
                    visitedDateTime: currentVisit.visitedDateTime
                        ? [...currentVisit.visitedDateTime, currentVisit.visitSchedule]
                        : [currentVisit.visitSchedule],
                };

                console.log('Updated visit data being sent:', updatedVisitData);
                await updateVisit(updatedVisitData);
                toast.success(`Visit marked as ${!currentVisit.visited ? "Visited" : "Not Visited"} successfully!`);
                
                // Refresh the visits data
                await fetchVisits(filterDate, timeFilter, selectedProject);
            }
        } catch (error) {
            console.error('Error updating visit:', error);
            toast.error('Failed to update visit status. Please try again.');
        } finally {
            setIsLoading(false);
            fetchVisits();

        }
    };


    const filteredVisits = useMemo(() => {
        if (!visits) return [];

        return visits.filter(visit => {
            const query = searchQuery.toLowerCase();
            return (
                (visit.customer && visit.customer.toLowerCase().includes(query)) ||
                (visit.mobileNoWa && visit.mobileNoWa.toLowerCase().includes(query)) ||
                (visit.configName && visit.configName.toLowerCase().includes(query)) ||
                (visit.projectName && visit.projectName.toLowerCase().includes(query)) ||
                (visit.ownerName && visit.ownerName.toLowerCase().includes(query)) ||
                (visit.details && visit.details.toLowerCase().includes(query))
            );
        });
    }, [visits, searchQuery]);
    const handleViewClick = (visit) => {
      

        // First, determine which lead category the visit belongs to
        let selectedLeadDataArray = null;
        let selectedLead = null;
        const userId = visit.visitData?.user?.userId;
        
        if (userId && leadsData) {
            if (leadsData.coldLeads.some(lead => lead.user_account?.user?.userId === userId)) {
                selectedLeadDataArray = leadsData.coldLeads;
                selectedLead = leadsData.coldLeads.find(lead => lead.user_account?.user?.userId === userId);
            } else if (leadsData.lukeWarmLeads.some(lead => lead.user_account?.user?.userId === userId)) {
                selectedLeadDataArray = leadsData.lukeWarmLeads;
                selectedLead = leadsData.lukeWarmLeads.find(lead => lead.user_account?.user?.userId === userId);
            } else if (leadsData.warmLeads.some(lead => lead.user_account?.user?.userId === userId)) {
                selectedLeadDataArray = leadsData.warmLeads;
                selectedLead = leadsData.warmLeads.find(lead => lead.user_account?.user?.userId === userId);
            } else if (leadsData.hotLeads.some(lead => lead.user_account?.user?.userId === userId)) {
                selectedLeadDataArray = leadsData.hotLeads;
                selectedLead = leadsData.hotLeads.find(lead => lead.user_account?.user?.userId === userId);
            } else if (leadsData.customers.some(lead => lead.user_account?.user?.userId === userId)) {
                selectedLeadDataArray = leadsData.customers;
                selectedLead = leadsData.customers.find(lead => lead.user_account?.user?.userId === userId);
            }
        }
        setSelectedVisitForView({
            visitData: visit.visitData,  // All visit data
            selectedLead: selectedLead    // Matched lead data from leeds
        });

        setSlecetedLeadsDataArray(selectedLead);


        setShowLeadDetails(true);
      ;
    };

    const VisitStatusCell = ({ row }) => {
        const [isLoading, setIsLoading] = useState(false);
        const visited = row.visited;
        const handleVisitClick = async (e) => {
            e.stopPropagation();
            setIsLoading(true);
            try {
                await handleMarkAsVisited(row, row.visitData);
            } catch (error) {
                console.error('Error marking as visited:', error);
                toast.error('Failed to update visit status. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };
        if (visited) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    VISITED
                </span>
            );
        }
        return (
            <OutlineButton onClick={handleVisitClick} disabled={isLoading}>
                {isLoading ? <Spinner className="h-4 w-4" /> : 'Mark visited'}
            </OutlineButton>
        );
    };

    const columns = useMemo(() => [
        { key: 'customer', header: 'Customer', width: 180, render: (r) => (
            <span className="text-sm font-medium text-slate-900 truncate block" title={r.customer}>
                {r.customer || '—'}
            </span>
        )},
        { key: 'mobile', header: 'WhatsApp', width: 140, render: (r) => (
            <span className="text-sm text-slate-700 tabular-nums">{r.mobileNoWa || '—'}</span>
        )},
        { key: 'config', header: 'Config', width: 160, render: (r) => (
            <span className="text-sm text-slate-700 truncate block" title={r.configName}>
                {r.configName || '—'}
            </span>
        )},
        { key: 'project', header: 'Project', width: 180, render: (r) => (
            <span className="text-sm text-slate-700 truncate block" title={r.projectName}>
                {r.projectName || '—'}
            </span>
        )},
        { key: 'agent', header: 'Agent', width: 150, render: (r) => (
            <span className="text-sm text-slate-700 truncate block" title={r.ownerName}>
                {r.ownerName || '—'}
            </span>
        )},
        { key: 'time', header: 'Time', width: 110, render: (r) => (
            <span className="text-xs text-slate-600 tabular-nums">{r.time || '—'}</span>
        )},
        { key: 'details', header: 'Details', width: 180, render: (r) => (
            <span className="text-xs text-slate-600 line-clamp-2" title={r.details}>
                {r.details || '—'}
            </span>
        )},
        { key: 'status', header: 'Visit status', width: 140, render: (r) => <VisitStatusCell row={r} /> },
        { key: 'view', header: 'View', width: 70, align: 'center', render: (r) => (
            <button
                type="button"
                onClick={() => handleViewClick(r)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-flamingo-600 hover:bg-flamingo-50 transition-colors"
                title="View"
                aria-label="View"
            >
                <Eye size={14} />
            </button>
        )},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [handleViewClick]);


    useEffect(() => {
        fetchVisits(filterDate, timeFilter, selectedProject);
    }, [filterDate, fetchVisits, timeFilter, selectedProject, activeTab]);


    const upcomingVisits = useMemo(() => {
        return filteredVisits.filter(visit => {
            const visitSchedule = visit.visitData?.visitSchedule;
            if (!visitSchedule) return false; // Skip visits without a visitSchedule

            const today = new Date().toISOString().slice(0, 10);
            return visitSchedule.slice(0, 10) >= today;
        });
    }, [filteredVisits]);

    const pastVisits = useMemo(() => {
        return filteredVisits.filter(visit => {
            // Determine if a visit is past based on visitedDateTime array
            return visit.visitData?.visitedDateTime && visit.visitData.visitedDateTime.length > 0;
        });
    }, [filteredVisits]);

    const currentTimeFilterOptions = useMemo(() => {
        return activeTab === 'upcoming' ? upcomingTimeFilterOptions : pastTimeFilterOptions;
    }, [activeTab]);
    const closeLeadDetails = () => {
        setShowLeadDetails(false);
        setSelectedVisitForView(null);
    };


    const visitedTodayCount = scheduledVisits;
    const totalVisits = visits.length;
    const notVisitedCount = visits.filter((visit) => !visit.visited).length;

    return (
        <div className="flex flex-col gap-6">
            {/* Hero band — slate gradient, same family as the rest of the app. */}
            <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 p-5 lg:p-6 text-white">
                <p className="text-xs uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                    <CalendarFold size={12} />
                    Sales
                </p>
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight mt-1">
                    Visit dashboard
                </h1>
                <p className="text-xs text-slate-300 mt-1">
                    Track upcoming and past site visits, recent walk-ins, and lead distribution
                </p>
            </div>

            {/* KPI tiles — three headline visit counts. */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                        <CheckBoxOutlined fontSize="small" />
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Visited today</p>
                        <p className="text-2xl font-bold text-slate-900 tabular-nums">{visitedTodayCount}</p>
                    </div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-flamingo-50 text-flamingo-600 flex items-center justify-center shrink-0">
                        <CalendarFold size={20} />
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Scheduled visits</p>
                        <p className="text-2xl font-bold text-slate-900 tabular-nums">{totalVisits}</p>
                    </div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
                        <CalendarFold size={20} />
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Not visited yet</p>
                        <p className="text-2xl font-bold text-slate-900 tabular-nums">{notVisitedCount}</p>
                    </div>
                </div>
            </div>

            {/* Recent visitors mini-table + leads bifurcation chart — paired cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                        <div>
                            <h3 className="text-base font-semibold text-slate-900">Recent visitors</h3>
                            <p className="text-xs text-slate-500 mt-0.5">Last 5 walk-ins</p>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse min-w-[480px]">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                                    <th className="text-left px-5 py-3 font-semibold">Name</th>
                                    <th className="text-left px-4 py-3 font-semibold">Config</th>
                                    <th className="text-left px-4 py-3 font-semibold">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {visits.slice(0, 5).map((visit, index) => (
                                    <tr key={index} className="hover:bg-slate-50/60 transition-colors">
                                        <td className="px-5 py-3 text-sm font-medium text-slate-900 truncate" title={visit.customer}>
                                            {visit.customer || "—"}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-700 truncate" title={visit.configName}>
                                            {visit.configName || "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            {visit.visited ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                                    VISITED
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border bg-amber-50 text-amber-700 border-amber-200">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                                    SCHEDULED
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {visits.length === 0 && (
                                    <tr>
                                        <td colSpan="3" className="px-5 py-8 text-center text-sm text-slate-500">
                                            No recent visitors yet
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-200">
                        <h3 className="text-base font-semibold text-slate-900">Leads bifurcation</h3>
                        <p className="text-xs text-slate-500 mt-0.5">Distribution across temperatures</p>
                    </div>
                    <div className="p-5">
                        {leadsData ? (
                            <div style={{ height: "260px" }}>
                                <canvas ref={chartRef} />
                            </div>
                        ) : (
                            <div className="h-[260px] flex items-center justify-center text-sm text-slate-500">
                                Loading leads data…
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Scheduled visits — pill sub-tabs + filter strip + DataTable */}
            <div className="flex flex-col gap-4">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5 inline-flex gap-1 self-start">
                    <button
                        type="button"
                        onClick={() => setActiveTab("upcoming")}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                            activeTab === "upcoming"
                                ? "bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm"
                                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                        }`}
                    >
                        Upcoming visits
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab("past")}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                            activeTab === "past"
                                ? "bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm"
                                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                        }`}
                    >
                        Past visits
                    </button>
                </div>

                {/* Filter strip — slate-friendly look. */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                        <div className="flex-1 min-w-[200px]">
                            <CustomInput
                                type="text"
                                className="w-full"
                                placeholder="Search agents, projects, configurations…"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <select
                            value={timeFilter}
                            onChange={handleTimeFilterChange}
                            className="px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:border-flamingo-500 focus:ring-1 focus:ring-flamingo-200 outline-none min-w-[140px]"
                        >
                            {currentTimeFilterOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <select
                            value={selectedProject}
                            onChange={handleProjectChange}
                            className="px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:border-flamingo-500 focus:ring-1 focus:ring-flamingo-200 outline-none min-w-[160px]"
                        >
                            <option value="">All projects</option>
                            {projects.map((project) => (
                                <option key={project.projectId} value={project.projectId}>
                                    {project.projectName}
                                </option>
                            ))}
                        </select>
                        <input
                            type="date"
                            id="filterDate"
                            value={filterDate}
                            onChange={handleDateChange}
                            className="px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:border-flamingo-500 focus:ring-1 focus:ring-flamingo-200 outline-none min-w-[150px]"
                        />
                    </div>
                </div>

                <DataTable
                    columns={columns}
                    data={activeTab === 'upcoming' ? upcomingVisits : pastVisits}
                    minWidth={1300}
                    rowKey={(r, i) => r?.visitData?.id ?? `${r?.customerSalesId ?? ''}-${i}`}
                    emptyState={
                        <div className="px-6 py-12 text-center">
                            <p className="text-base font-semibold text-slate-900">
                                {activeTab === 'upcoming' ? 'No upcoming visits' : 'No past visits'}
                            </p>
                            <p className="text-sm text-slate-500 mt-1">
                                {activeTab === 'upcoming'
                                    ? 'Visits will appear here as soon as customers schedule one.'
                                    : 'Completed visits will appear here.'}
                            </p>
                        </div>
                    }
                />
            </div>
            {/* LeadDetailsDrawer (Conditionally Rendered) */}
            {showLeadDetails && selectedVisitForView && (
                <LeadDetailsDrawer
                    open={showLeadDetails}
                    onClose={closeLeadDetails}
                    user={selectedVisitForView}

                />
            )}
        </div>
    );
}

export default DashboardContent;